import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  MenuItem,
  Divider,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  ExpandMore,
  AccessTime,
  CheckCircle,
  PendingActions,
  PlayArrow,
  Pause,
  Download,
  Upload,
  Rocket,
  GpsFixed,
  Preview,
  Search,
  Analytics,
} from '@mui/icons-material';

/**
 * Advanced Campaign Planner Component
 * Complete workflow: Templates -> Accounts -> Import -> Schedule -> Review
 */
const CampaignPlanner = ({
  plans = [],
  campaigns = [],
  accounts = [],
  accountGroups = [],
  templates = [],
  onCreatePlan = () => {},
  onEditPlan = () => {},
  onDeletePlan = () => {},
  onSchedulePlan = () => {},
  onApplySegmentation = () => {},
  onRecipientColumns = () => {},
  onLaunchCampaign = () => {},
  onPauseCampaign = () => {},
  planStats = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State Management
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const [failedDialogPlan, setFailedDialogPlan] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingDialogPlan, setTrackingDialogPlan] = useState(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templatesDialogPlan, setTemplatesDialogPlan] = useState(null);
  const [accountsDialogOpen, setAccountsDialogOpen] = useState(false);
  const [accountsDialogPlan, setAccountsDialogPlan] = useState(null);
  const [filterTab, setFilterTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState('');
  const [recipientFileError, setRecipientFileError] = useState('');
  const templateColumns = [
    'Legal Name',
    'DBA',
    'DOT',
    'MC',
    'State(Physical)',
    'State(Mailing)',
    'Phone',
    'Email',
    'Status',
    'Carrier Operation',
    'Power Units',
    'Drivers',
    'Active Duration',
    'Physical Address',
    'Mailing Address',
  ];

  // Form state for new plan
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedTemplates: [],
    selectedAccounts: [],
    segmentType: 'all',
    recipientGroups: [],
    scheduledDate: '',
    timezone: 'UTC',
    recipientLimit: '',
    sendingFrequency: 'immediate',
    retryCount: 3,
    autoRespond: false,
    trackOpens: true,
    trackClicks: true,
    recipientFileName: '',
    recipientRows: [],
    delayMode: 'fixed',
    delaySeconds: 30,
    minDelaySec: 20,
    maxDelaySec: 60,
    rotateAccounts: true,
    shuffleTemplates: true,
    jitterSec: 5,
    batchPauseEvery: 10,
    batchPauseSec: 120,
    maxPerAccountPerHour: 40,
    maxFailures: 5,
    enforceMinDelay: true,
  });

  const wizardSteps = ['Template', 'Accounts', 'Import', 'Anti-Spam', 'Schedule', 'Review'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#2196F3';
      case 'running': return '#4CAF50';
      case 'completed': return '#FF9800';
      case 'draft': return '#667eea';
      case 'paused': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <AccessTime />;
      case 'running': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'draft': return <PendingActions />;
      case 'paused': return <Pause />;
      default: return <MoreVert />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const countRecipientsFromPlan = (plan) => {
    const rows = Array.isArray(plan?.recipientRows)
      ? plan.recipientRows
      : (Array.isArray(plan?.recipients) ? plan.recipients : []);
    if (!rows.length) return 0;
    let count = 0;
    rows.forEach((row) => {
      if (!row) return;
      if (Array.isArray(row)) {
        row.forEach((cell) => {
          const email = String(cell || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      if (typeof row === 'string') {
        String(row)
          .split(/[\n,;]+/)
          .map((entry) => entry.trim())
          .filter((entry) => entry.includes('@'))
          .forEach(() => {
            count += 1;
          });
        return;
      }
      if (typeof row !== 'object') return;
      const keys = Object.keys(row);
      const emailKey =
        keys.find((key) => /email/i.test(key)) ||
        keys.find((key) => /e-mail/i.test(key)) ||
        keys.find((key) => /mail/i.test(key));
      if (!emailKey) {
        Object.values(row).forEach((cell) => {
          const email = String(cell || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      const value = row[emailKey];
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          const email = String(entry || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      String(value || '')
        .split(/[\n,;]+/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.includes('@'))
        .forEach(() => {
          count += 1;
        });
    });
    return count || rows.length;
  };

  const downloadPlanRecipients = (plan) => {
    const rows = Array.isArray(plan?.recipientRows)
      ? plan.recipientRows
      : (Array.isArray(plan?.recipients) ? plan.recipients : []);
    if (!rows.length) return;
    const firstRow = rows[0];
    const headers = Array.isArray(firstRow)
      ? firstRow.map((_, idx) => `Column ${idx + 1}`)
      : Object.keys(firstRow || {});
    if (!headers.length) return;
    const csvLines = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((key) => {
            const value = Array.isArray(row)
              ? row[Number(key.replace('Column ', '')) - 1]
              : row?.[key] ?? '';
            const cell = String(value).replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(',')
      ),
    ].join('\n');
    const safeName = String(plan?.name || 'campaign').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
    const filename = `${safeName || 'campaign'}_recipients.csv`;
    const blob = new Blob([`${csvLines}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleWizardNext = () => {
    if (wizardStep < wizardSteps.length - 1) {
      setWizardStep(wizardStep + 1);
    }
  };

  const handleWizardPrev = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
    }
  };

  const handleHeaderAction = (action) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const handleCreatePlan = () => {
    const delayMode = formData.delayMode === 'range' ? 'range' : 'fixed';
    const fixedDelay = Math.max(0, Number(formData.delaySeconds) || 0);
    const minDelay = Math.max(0, Number(formData.minDelaySec) || 0);
    const maxDelay = Math.max(minDelay, Number(formData.maxDelaySec) || 0);
    const jitterSec = Math.max(0, Number(formData.jitterSec) || 0);
    const batchPauseEvery = Math.max(0, Number(formData.batchPauseEvery) || 0);
    const batchPauseSec = Math.max(0, Number(formData.batchPauseSec) || 0);
    const maxPerAccountPerHour = Math.max(0, Number(formData.maxPerAccountPerHour) || 0);
    const maxFailures = Math.max(0, Number(formData.maxFailures) || 0);

    onCreatePlan({
      name: formData.name,
      description: formData.description,
      selectedTemplates: formData.selectedTemplates || [],
      selectedAccounts: formData.selectedAccounts || [],
      segmentType: formData.segmentType,
      recipientGroups: formData.recipientGroups || [],
      scheduledDate: formData.scheduledDate,
      timezone: formData.timezone,
      recipientLimit: formData.recipientLimit,
      sendingFrequency: formData.sendingFrequency,
      retryCount: formData.retryCount,
      autoRespond: formData.autoRespond,
      trackOpens: formData.trackOpens,
      trackClicks: formData.trackClicks,
      recipientFileName: formData.recipientFileName || '',
      recipientRows: formData.recipientRows || [],
      minDelaySec: delayMode === 'range' ? minDelay : fixedDelay,
      maxDelaySec: delayMode === 'range' ? maxDelay : fixedDelay,
      rotateAccounts: formData.rotateAccounts !== false,
      shuffleTemplates: formData.shuffleTemplates !== false,
      jitterSec,
      batchPauseEvery,
      batchPauseSec,
      maxPerAccountPerHour,
      maxFailures,
      enforceMinDelay: formData.enforceMinDelay !== false,
    });
    setFormData({
      name: '',
      description: '',
      selectedTemplates: [],
      selectedAccounts: [],
      segmentType: 'all',
      recipientGroups: [],
      scheduledDate: '',
      timezone: 'UTC',
      recipientLimit: '',
      sendingFrequency: 'immediate',
      retryCount: 3,
      autoRespond: false,
      trackOpens: true,
      trackClicks: true,
      recipientFileName: '',
      recipientRows: [],
      delayMode: 'fixed',
      delaySeconds: 30,
      minDelaySec: 20,
      maxDelaySec: 60,
      rotateAccounts: true,
      shuffleTemplates: true,
      jitterSec: 5,
      batchPauseEvery: 10,
      batchPauseSec: 120,
      maxPerAccountPerHour: 40,
      maxFailures: 5,
      enforceMinDelay: true,
    });
    setWizardStep(0);
    setWizardOpen(false);
  };

  // Filter plans based on tab
  const filteredPlans = plans.filter((plan) => {
    const statusMap = ['', 'draft', 'scheduled', 'running', 'completed'];
    const matchesTab = filterTab === 0 || plan.status === statusMap[filterTab];
    const matchesSearch = plan.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const statusCounts = plans.reduce(
    (acc, plan) => {
      acc.all += 1;
      const status = plan?.status || 'draft';
      if (!acc[status]) acc[status] = 0;
      acc[status] += 1;
      return acc;
    },
    { all: 0, draft: 0, scheduled: 0, running: 0, completed: 0, paused: 0, failed: 0 }
  );

  const renderTabLabel = (label, count, icon, color) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 999,
          bgcolor: color,
          color: '#fff',
          fontSize: '0.7rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="caption" fontWeight={700}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const filteredAccounts = accounts.filter((account) => {
    if (!accountSearch) return true;
    const hay = `${account.label || ''} ${account.fromEmail || ''} ${account.username || ''}`.toLowerCase();
    return hay.includes(accountSearch.toLowerCase());
  });

  const selectedStats = selectedPlan ? (planStats[selectedPlan.id] || {}) : {};
  const previewTemplate = selectedPlan
    ? (selectedPlan.templates && selectedPlan.templates.length
      ? selectedPlan.templates[0]
      : templates.find((t) => t.id === selectedPlan?.strategy?.selectedTemplates?.[0]))
    : null;

  const addAccountsToSelection = (ids) => {
    const next = Array.from(new Set([...(formData.selectedAccounts || []), ...(ids || [])]));
    setFormData({ ...formData, selectedAccounts: next });
  };

  const handleRecipientFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setRecipientFileError('');
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rows.length) {
        throw new Error('No rows found in the uploaded file.');
      }
      const headers = (rows[0] || []).map((h, idx) => {
        const label = String(h || '').trim();
        return label || `Column ${idx + 1}`;
      });
      const dataRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim() !== ''));
      const mappedRows = dataRows.map((row) => {
        const record = {};
        headers.forEach((key, idx) => {
          record[key] = row[idx] ?? '';
        });
        return record;
      });
      setFormData((prev) => ({
        ...prev,
        recipientFileName: file.name,
        recipientRows: mappedRows,
      }));
      onRecipientColumns(headers);
    } catch (err) {
      setRecipientFileError(err.message || 'Failed to read file');
      setFormData((prev) => ({ ...prev, recipientFileName: '', recipientRows: [] }));
    } finally {
      event.target.value = '';
    }
  };

  const downloadRecipientTemplate = () => {
    const header = templateColumns.join(',');
    const rows = [
      ['Alpha Logistics LLC', 'Alpha Logistics', '1234567', 'MC123456', 'TX', 'TX', '0300-1234567', 'dispatch@alphalogistics.com', 'Active', 'Carrier', '25', '30', '3 years', '123 Main St, Dallas, TX', 'PO Box 45, Dallas, TX'],
      ['Blue Ridge Freight', 'Blue Ridge', '7654321', 'MC765432', 'CA', 'CA', '0312-5550188', 'ops@blueridgefreight.com', 'Active', 'Carrier', '12', '14', '1 year', '456 Market St, San Francisco, CA', '456 Market St, San Francisco, CA'],
      ['Sunset Transport Inc', 'Sunset', '2468135', 'MC246813', 'IL', 'IL', '0321-5550147', 'team@sunsettransport.com', 'Inactive', 'Broker', '8', '10', '6 months', '789 Lake Shore Dr, Chicago, IL', '789 Lake Shore Dr, Chicago, IL'],
    ];
    const csvLines = [header, ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([`${csvLines}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'campaign_recipients_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* ====== HEADER SECTION ====== */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
          borderLeft: '4px solid #667eea',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Rocket sx={{ fontSize: 28, color: '#667eea' }} />
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                  Campaign Planner Pro
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Template {'->'} Accounts {'->'} Import {'->'} Anti-Spam {'->'} Schedule {'->'} Review
              </Typography>
            </Box>
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<Add />}
              onClick={() => setWizardOpen(true)}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {isMobile ? 'New' : 'Create Campaign'}
            </Button>
          </Stack>

          {/* Dashboard Stats */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">Total Plans</Typography>
                <Typography fontWeight={700} variant="h6">{plans.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#e3f2fd' }}>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'scheduled').length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">Running</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'running').length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'completed').length}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ====== FILTER & SEARCH ====== */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          placeholder="Search campaigns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          fullWidth={isMobile}
          sx={{ flex: 1 }}
        />
        <Tabs
          value={filterTab}
          onChange={(e, newValue) => setFilterTab(newValue)}
          sx={{ borderBottom: '1px solid #eee', overflow: 'auto' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={renderTabLabel('All', statusCounts.all, <MoreVert fontSize="small" />, '#6c63ff')} />
          <Tab label={renderTabLabel('Draft', statusCounts.draft, <PendingActions fontSize="small" />, '#ff9800')} />
          <Tab label={renderTabLabel('Scheduled', statusCounts.scheduled, <AccessTime fontSize="small" />, '#2196f3')} />
          <Tab label={renderTabLabel('Running', statusCounts.running, <PlayArrow fontSize="small" />, '#4caf50')} />
          <Tab label={renderTabLabel('Completed', statusCounts.completed, <CheckCircle fontSize="small" />, '#8e24aa')} />
        </Tabs>
      </Stack>

      {/* ====== PLANS LIST ====== */}
      {filteredPlans.length === 0 ? (
        <Alert
          severity="info"
          icon={<GpsFixed />}
          sx={{
            background: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
          }}
        >
          <Typography fontWeight={700}>No campaigns found</Typography>
          <Typography variant="caption">Create a new campaign to get started</Typography>
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {filteredPlans.map((plan) => {
            const stats = planStats[plan.id] || {
              totalRecipients: 0,
              opens: 0,
              clicks: 0,
              conversions: 0,
              estimatedEngagement: 0,
              campaignsIncluded: 0,
              accountsUsed: 0,
            };
            const derivedRecipients = stats.totalRecipients || countRecipientsFromPlan(plan);
            const derivedSegment = plan.segmentType || plan.strategy?.segmentType || 'All';
            const engagementRate = stats.estimatedEngagement
              || (stats.totalRecipients
                ? Math.round(((stats.opens || 0) / Math.max(stats.totalRecipients, 1)) * 100)
                : 0);
            const selectedTemplateIds = plan.strategy?.selectedTemplates || plan.selectedTemplates || [];
            const selectedAccountIds = plan.strategy?.selectedAccounts || plan.selectedAccounts || [];
            const planTemplates = (plan.templates && plan.templates.length)
              ? plan.templates
              : templates.filter((tmpl) => selectedTemplateIds.includes(tmpl.id));
            const planAccounts = accounts.filter((acc) => selectedAccountIds.includes(acc.id));

            return (
              <Accordion key={plan.id} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%', pr: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 1,
                        background: `${getStatusColor(plan.status)}20`,
                        display: 'flex',
                      }}>
                        {getStatusIcon(plan.status)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={700} variant="body2">
                          {plan.name || 'Unnamed Campaign'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={plan.status?.toUpperCase()}
                            sx={{
                              backgroundColor: getStatusColor(plan.status) + '30',
                              color: getStatusColor(plan.status),
                              fontWeight: 700,
                            }}
                          />
                          {selectedTemplateIds.length > 0 && (
                            <Chip
                              size="small"
                              label={`${selectedTemplateIds.length} templates`}
                              variant="outlined"
                              onClick={handleHeaderAction(() => {
                                setTemplatesDialogPlan(plan);
                                setTemplatesDialogOpen(true);
                              })}
                            />
                          )}
                          {selectedAccountIds.length > 0 && (
                            <Chip
                              size="small"
                              label={`${selectedAccountIds.length} accounts`}
                              onClick={handleHeaderAction(() => {
                                setAccountsDialogPlan(plan);
                                setAccountsDialogOpen(true);
                              })}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    {!isMobile && engagementRate ? (
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#4CAF50' }}>
                        ~{Math.round(engagementRate || 0)}% engagement
                      </Typography>
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent="flex-end"
                      sx={{ rowGap: 1 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Preview />}
                        onClick={handleHeaderAction(() => {
                          setSelectedPlan(plan);
                          setPreviewDialog(true);
                        })}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Analytics />}
                        onClick={handleHeaderAction(() => {
                          setSelectedPlan(plan);
                          setAnalyticsDialog(true);
                        })}
                      >
                        Analytics
                      </Button>
                      {(plan.status === 'draft' || plan.status === 'completed' || plan.status === 'failed' || plan.status === 'paused') && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Rocket />}
                          onClick={handleHeaderAction(() => onLaunchCampaign(plan.id))}
                          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                        >
                          {plan.status === 'draft' ? 'Launch' : 'Run again'}
                        </Button>
                      )}
                      {plan.status === 'running' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Pause />}
                          onClick={handleHeaderAction(() => onPauseCampaign(plan.id))}
                        >
                          Pause
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={handleHeaderAction(() => onEditPlan(plan))}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleHeaderAction(() => onDeletePlan(plan.id))}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack spacing={2}>
                    {/* Stats Grid */}
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#f5f5f5', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Recipients</Typography>
                          <Typography fontWeight={700}>{derivedRecipients}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#e3f2fd', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Opens</Typography>
                          <Typography fontWeight={700}>{stats.opens || 0}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#e8f5e9', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Clicks</Typography>
                          <Typography fontWeight={700}>{stats.clicks || 0}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#fff3e0', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Conversions</Typography>
                          <Typography fontWeight={700}>{stats.conversions || 0}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {stats.totalRecipients && (stats.sent || stats.failed) ? (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Sending progress
                          </Typography>
                          <Typography variant="caption">
                            {stats.sent || 0}/{stats.totalRecipients}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            ((stats.sent || 0) / Math.max(stats.totalRecipients || 1, 1)) * 100
                          )}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                        {stats.failed ? (
                          <Typography variant="caption" color="error">
                            Failed: {stats.failed}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}

                    {stats.failedRecipients && stats.failedRecipients.length ? (
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={handleHeaderAction(() => {
                            setFailedDialogPlan(plan);
                            setFailedDialogOpen(true);
                          })}
                        >
                          Failed recipients ({stats.failedRecipients.length})
                        </Button>
                      </Box>
                    ) : null}
                    {stats.trackingWarnings && stats.trackingWarnings.length ? (
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={handleHeaderAction(() => {
                            setTrackingDialogPlan(plan);
                            setTrackingDialogOpen(true);
                          })}
                        >
                          Tracking issues ({stats.trackingWarnings.length})
                        </Button>
                      </Box>
                    ) : null}

                    <Divider />

                    {/* Campaign Details */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Campaign Info</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>Templates: <strong>{(plan.strategy?.selectedTemplates || plan.selectedTemplates || []).length || 0}</strong></Typography>
                                <Typography>Accounts: <strong>{(plan.strategy?.selectedAccounts || plan.selectedAccounts || []).length || 0}</strong></Typography>
                                <Typography>Segment: <strong>{derivedSegment}</strong></Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>Recipients: <strong>{derivedRecipients}</strong></Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Typography>Recipients file:</Typography>
                                  {plan.recipientFileName ? (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={handleHeaderAction(() => downloadPlanRecipients(plan))}
                                    >
                                      {plan.recipientFileName}
                                    </Button>
                                  ) : (
                                    <Typography component="span"><strong>None</strong></Typography>
                                  )}
                                </Stack>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Schedule Info</Typography>
                          <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                            <Typography>Scheduled: <strong>{formatDate(plan.scheduledDate)}</strong></Typography>
                            <Typography>Timezone: <strong>{plan.timezone || 'UTC'}</strong></Typography>
                            <Typography>Frequency: <strong>{plan.sendingFrequency || 'Immediate'}</strong></Typography>
                          </Stack>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Anti-Spam Settings</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>
                                  Delay: <strong>{Number(plan.strategy?.minDelaySec || 0)}-{Number(plan.strategy?.maxDelaySec || 0)}s</strong>
                                </Typography>
                                <Typography>Jitter: <strong>{Number(plan.strategy?.jitterSec || 0)}s</strong></Typography>
                                <Typography>
                                  Rotate accounts: <strong>{plan.strategy?.rotateAccounts ? 'Yes' : 'No'}</strong>
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>
                                  Shuffle templates: <strong>{plan.strategy?.shuffleTemplates ? 'Yes' : 'No'}</strong>
                                </Typography>
                                <Typography>
                                  Max/hour: <strong>{Number(plan.strategy?.maxPerAccountPerHour || 0)}</strong>
                                </Typography>
                                <Typography>
                                  Pause: <strong>{Number(plan.strategy?.batchPauseEvery || 0)} emails / {Number(plan.strategy?.batchPauseSec || 0)}s</strong>
                                </Typography>
                                <Typography>
                                  Stop after failures: <strong>{Number(plan.strategy?.maxFailures || 0)}</strong>
                                </Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Performance Chart */}
                    <Box>
                      <Typography fontWeight={700} variant="body2" mb={1}>Performance</Typography>
                      <Box sx={{
                        p: 1.5,
                        background: '#f5f5f5',
                        borderRadius: 1,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                        gap: 1,
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Open Rate</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.opens / (stats.totalRecipients || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Click Rate</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.clicks / (stats.opens || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Conversion</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.conversions / (stats.clicks || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Engagement</Typography>
                          <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>{Math.round(engagementRate || 0)}%</Typography>
                        </Box>
                      </Box>
                    </Box>

                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      {/* ====== CREATE CAMPAIGN WIZARD ====== */}
      <Dialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={800}>Create New Campaign</Typography>
            <Typography variant="body2" color="text.secondary">
              Build a campaign in a few steps: templates, accounts, recipients file, schedule, review.
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
            {wizardSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {wizardStep === 0 && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Spring Sale 2024"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
              <FormControl fullWidth>
                <InputLabel>Select Templates</InputLabel>
                <Select
                  multiple
                  value={formData.selectedTemplates}
                  onChange={(e) => setFormData({ ...formData, selectedTemplates: e.target.value })}
                  label="Select Templates"
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => templates.find((t) => t.id === id)?.name || id)
                      .join(', ')
                  }
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {wizardStep === 1 && (
            <Stack spacing={2}>
              <Typography fontWeight={700} variant="body2">Select Sending Accounts</Typography>
              <TextField
                size="small"
                placeholder="Search accounts..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                sx={{ maxWidth: 360 }}
              />
              {accountGroups.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">Account Groups</Typography>
                  {accountGroups.map((group) => {
                    const groupIds = group.accountIds || [];
                    const inSelection = groupIds.every((id) =>
                      (formData.selectedAccounts || []).includes(id)
                    );
                    return (
                      <Card key={group.id} variant="outlined" sx={{ p: 1 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={group.name} />
                            <Typography variant="caption" color="text.secondary">
                              {groupIds.length} accounts
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExpandedGroupId(expandedGroupId === group.id ? '' : group.id)}
                            >
                              {expandedGroupId === group.id ? 'Hide' : 'View'}
                            </Button>
                            <Button
                              size="small"
                              variant={inSelection ? 'outlined' : 'contained'}
                              onClick={() => addAccountsToSelection(groupIds)}
                              disabled={!groupIds.length}
                            >
                              {inSelection ? 'Added' : 'Add group'}
                            </Button>
                          </Stack>
                        </Stack>
                        <Collapse in={expandedGroupId === group.id} unmountOnExit>
                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {groupIds.map((id) => {
                              const account = accounts.find((a) => a.id === id);
                              if (!account) return null;
                              return (
                                <Stack key={id} direction="row" spacing={1} alignItems="center">
                                  <Checkbox
                                    size="small"
                                    checked={formData.selectedAccounts?.includes(id)}
                                    onChange={(e) => {
                                      const updated = e.target.checked
                                        ? [...(formData.selectedAccounts || []), id]
                                        : formData.selectedAccounts?.filter((item) => item !== id);
                                      setFormData({ ...formData, selectedAccounts: updated });
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {account.label || account.fromEmail || account.username || account.id}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Collapse>
                      </Card>
                    );
                  })}
                </Stack>
              )}
              <Divider />
              <Stack spacing={1} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {filteredAccounts.map((account) => (
                  <FormControlLabel
                    key={account.id}
                    control={
                      <Checkbox
                        checked={formData.selectedAccounts?.includes(account.id)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...(formData.selectedAccounts || []), account.id]
                            : formData.selectedAccounts?.filter((id) => id !== account.id);
                          setFormData({ ...formData, selectedAccounts: updated });
                        }}
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant="body2" fontWeight={700}>
                          {account.label || account.fromEmail || account.username || account.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.fromEmail || account.username || ''}
                        </Typography>
                      </Stack>
                    }
                  />
                ))}
                {filteredAccounts.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No accounts match this search.
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}

          {wizardStep === 2 && (
            <Stack spacing={2}>
              <Typography fontWeight={700} variant="body2">Import Recipients File</Typography>
              <Alert severity="info">
                Upload CSV or Excel with columns like email, name, truck type, etc.
              </Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                <Button variant="outlined" component="label" startIcon={<Upload />}>
                  Upload CSV/Excel
                  <input
                    type="file"
                    hidden
                    accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleRecipientFileUpload}
                  />
                </Button>
                <Button variant="outlined" startIcon={<Download />} onClick={downloadRecipientTemplate}>
                  Download template
                </Button>
              </Stack>
              {recipientFileError && <Alert severity="error">{recipientFileError}</Alert>}
              {formData.recipientFileName && (
                <Typography variant="body2">
                  File: <strong>{formData.recipientFileName}</strong> • Rows:{' '}
                  <strong>{formData.recipientRows.length}</strong>
                </Typography>
              )}
              {formData.recipientRows.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(formData.recipientRows[0]).slice(0, 4).map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.recipientRows.slice(0, 5).map((row, idx) => (
                        <TableRow key={idx}>
                          {Object.keys(formData.recipientRows[0]).slice(0, 4).map((key) => (
                            <TableCell key={key}>{String(row[key] ?? '')}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          )}

          {wizardStep === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography fontWeight={700} variant="body2">Anti-Spam Controls</Typography>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">Timing</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Delay Mode</InputLabel>
                              <Select
                                value={formData.delayMode}
                                onChange={(e) => setFormData({ ...formData, delayMode: e.target.value })}
                                label="Delay Mode"
                              >
                                <MenuItem value="fixed">Fixed delay</MenuItem>
                                <MenuItem value="range">Random range</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {formData.delayMode === 'range' ? (
                            <>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Min (sec)"
                                  value={formData.minDelaySec}
                                  onChange={(e) => setFormData({ ...formData, minDelaySec: e.target.value })}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Max (sec)"
                                  value={formData.maxDelaySec}
                                  onChange={(e) => setFormData({ ...formData, maxDelaySec: e.target.value })}
                                />
                              </Grid>
                            </>
                          ) : (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Fixed (sec)"
                                value={formData.delaySeconds}
                                onChange={(e) => setFormData({ ...formData, delaySeconds: e.target.value })}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Jitter (sec)"
                              value={formData.jitterSec}
                              onChange={(e) => setFormData({ ...formData, jitterSec: e.target.value })}
                            />
                          </Grid>
                        </Grid>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.enforceMinDelay}
                              onChange={(e) => setFormData({ ...formData, enforceMinDelay: e.target.checked })}
                            />
                          }
                          label="Enforce safe minimum delay"
                        />
                      </Stack>
                      <Divider />
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={5}>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">Rotation</Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.rotateAccounts}
                                  onChange={(e) => setFormData({ ...formData, rotateAccounts: e.target.checked })}
                                />
                              }
                              label="Rotate accounts"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.shuffleTemplates}
                                  onChange={(e) => setFormData({ ...formData, shuffleTemplates: e.target.checked })}
                                />
                              }
                              label="Shuffle templates"
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <Stack spacing={0.75}>
                            <Typography variant="caption" color="text.secondary">Guardrails</Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Max/hour"
                                  value={formData.maxPerAccountPerHour}
                                  onChange={(e) => setFormData({ ...formData, maxPerAccountPerHour: e.target.value })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Stop after fails"
                                  value={formData.maxFailures}
                                  onChange={(e) => setFormData({ ...formData, maxFailures: e.target.value })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Pause every N"
                                  value={formData.batchPauseEvery}
                                  onChange={(e) => setFormData({ ...formData, batchPauseEvery: e.target.value })}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Pause (sec)"
                                  value={formData.batchPauseSec}
                                  onChange={(e) => setFormData({ ...formData, batchPauseSec: e.target.value })}
                                />
                              </Grid>
                            </Grid>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography fontWeight={700} variant="body2">Quick guide</Typography>
                      <Divider />
                      <Stack spacing={1.25}>
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <AccessTime fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Delay</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Gap between emails. Start 30-90s.
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: 'info.light',
                              color: 'info.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PendingActions fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Jitter</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Small random +/- seconds so timing looks human.
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: 'warning.light',
                              color: 'warning.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <GpsFixed fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Guardrails</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Caps and pauses that limit volume to avoid spam flags.
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: 'success.light',
                              color: 'success.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircle fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Max/hour + Pause</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Per account hourly limit and break after N sends.
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: 'secondary.light',
                              color: 'secondary.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Rocket fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Rotate + Shuffle</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Spread load across accounts and templates.
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {wizardStep === 4 && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Schedule Date & Time"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="EST">Eastern Standard</MenuItem>
                  <MenuItem value="CST">Central Standard</MenuItem>
                  <MenuItem value="PST">Pacific Standard</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Sending Frequency</InputLabel>
                <Select
                  value={formData.sendingFrequency}
                  onChange={(e) => setFormData({ ...formData, sendingFrequency: e.target.value })}
                  label="Sending Frequency"
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {wizardStep === 5 && (
            <Stack spacing={2}>
              <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                <Typography fontWeight={700} variant="body2" mb={1}>Campaign Summary</Typography>
                <Stack spacing={1} sx={{ fontSize: '0.85rem' }}>
                  <Typography>Name: <strong>{formData.name}</strong></Typography>
                  <Typography>Templates: <strong>{formData.selectedTemplates?.length || 0}</strong></Typography>
                  <Typography>Accounts: <strong>{formData.selectedAccounts?.length || 0}</strong></Typography>
                  <Typography>Recipient file: <strong>{formData.recipientFileName || 'None'}</strong></Typography>
                  <Typography>Rows: <strong>{formData.recipientRows?.length || 0}</strong></Typography>
                  <Typography>
                    Delay: <strong>{
                      formData.delayMode === 'range'
                        ? `${formData.minDelaySec || 0}-${formData.maxDelaySec || 0}s`
                        : `${formData.delaySeconds || 0}s`
                    }</strong>
                  </Typography>
                  <Typography>Jitter: <strong>{formData.jitterSec || 0}s</strong></Typography>
                  <Typography>Rotate accounts: <strong>{formData.rotateAccounts ? 'Yes' : 'No'}</strong></Typography>
                  <Typography>Shuffle templates: <strong>{formData.shuffleTemplates ? 'Yes' : 'No'}</strong></Typography>
                  <Typography>Max/hour: <strong>{formData.maxPerAccountPerHour || 0}</strong></Typography>
                  <Typography>Pause: <strong>{formData.batchPauseEvery || 0} emails / {formData.batchPauseSec || 0}s</strong></Typography>
                  <Typography>Stop after failures: <strong>{formData.maxFailures || 0}</strong></Typography>
                  <Typography>Scheduled: <strong>{formatDate(formData.scheduledDate)}</strong></Typography>
                  <Typography>Timezone: <strong>{formData.timezone}</strong></Typography>
                </Stack>
              </Paper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.trackOpens}
                    onChange={(e) => setFormData({ ...formData, trackOpens: e.target.checked })}
                  />
                }
                label="Track Opens"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.trackClicks}
                    onChange={(e) => setFormData({ ...formData, trackClicks: e.target.checked })}
                  />
                }
                label="Track Clicks"
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setWizardOpen(false)}>Cancel</Button>
          <Button
            disabled={wizardStep === 0}
            onClick={handleWizardPrev}
            variant="outlined"
          >
            Back
          </Button>
          {wizardStep < wizardSteps.length - 1 ? (
            <Button
              onClick={handleWizardNext}
              variant="contained"
              disabled={!formData.name || (formData.selectedTemplates || []).length === 0}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreatePlan}
              variant="contained"
              startIcon={<Rocket />}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Create Campaign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ====== PREVIEW DIALOG ====== */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Campaign Preview</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                <Typography fontWeight={700} mb={1}>Email Preview</Typography>
                <Box sx={{
                  p: 2,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {previewTemplate?.body ? (
                    <Box
                      sx={{ width: '100%' }}
                      dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
                    />
                  ) : (
                    <Typography color="text.secondary">No template selected for this campaign.</Typography>
                  )}
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ====== ANALYTICS DIALOG ====== */}
      <Dialog open={analyticsDialog} onClose={() => setAnalyticsDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Campaign Analytics</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            (() => {
              const totalRecipients = selectedStats.totalRecipients || 0;
              const opens = selectedStats.opens || 0;
              const clicks = selectedStats.clicks || 0;
              const openRate = totalRecipients ? Math.round((opens / totalRecipients) * 100) : 0;
              const clickRate = totalRecipients ? Math.round((clicks / totalRecipients) * 100) : 0;
              return (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    {!totalRecipients ? (
                      <Alert severity="info">No analytics yet. Launch the campaign to start tracking opens.</Alert>
                    ) : null}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, background: '#e3f2fd' }}>
                      <Typography variant="caption" color="text.secondary">Open Rate</Typography>
                      <Typography fontWeight={700} variant="h6">{openRate}%</Typography>
                      <LinearProgress variant="determinate" value={openRate} sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, background: '#e8f5e9' }}>
                      <Typography variant="caption" color="text.secondary">Click Rate</Typography>
                      <Typography fontWeight={700} variant="h6">{clickRate}%</Typography>
                      <LinearProgress variant="determinate" value={clickRate} sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Sent</Typography>
                          <Typography fontWeight={700}>{totalRecipients}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Opens</Typography>
                          <Typography fontWeight={700}>{opens}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Clicks</Typography>
                          <Typography fontWeight={700}>{clicks}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Last open</Typography>
                          <Typography fontWeight={700}>{selectedStats.lastOpenedAt || '—'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              );
            })()
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Failed Recipients</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(failedDialogPlan && (planStats[failedDialogPlan.id]?.failedRecipients || []).length)
              ? (planStats[failedDialogPlan.id]?.failedRecipients || []).map((item, idx) => (
                <Paper key={`${item.email}-${idx}`} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{item.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.error || 'Send failed'}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No failed recipients.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFailedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={trackingDialogOpen} onClose={() => setTrackingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tracking Issues</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(trackingDialogPlan && (planStats[trackingDialogPlan.id]?.trackingWarnings || []).length)
              ? (planStats[trackingDialogPlan.id]?.trackingWarnings || []).map((item, idx) => (
                <Paper key={`${item.email}-${idx}`} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{item.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.error || 'Tracking error'}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No tracking issues.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTrackingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templatesDialogOpen} onClose={() => setTemplatesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Templates</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(templatesDialogPlan && ((templatesDialogPlan.templates && templatesDialogPlan.templates.length) || (templatesDialogPlan.strategy?.selectedTemplates || []).length))
              ? (templatesDialogPlan.templates && templatesDialogPlan.templates.length
                ? templatesDialogPlan.templates
                : templates.filter((tmpl) => (templatesDialogPlan.strategy?.selectedTemplates || []).includes(tmpl.id))
              ).map((tmpl) => (
                <Paper key={tmpl.id} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{tmpl.name || 'Untitled template'}</Typography>
                  {tmpl.subject ? (
                    <Typography variant="caption" color="text.secondary">{tmpl.subject}</Typography>
                  ) : null}
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No templates selected.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTemplatesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accountsDialogOpen} onClose={() => setAccountsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Accounts</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(accountsDialogPlan && (accountsDialogPlan.strategy?.selectedAccounts || accountsDialogPlan.selectedAccounts || []).length)
              ? accounts.filter((acc) => (accountsDialogPlan.strategy?.selectedAccounts || accountsDialogPlan.selectedAccounts || []).includes(acc.id)).map((acc) => (
                <Paper key={acc.id} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{acc.label || acc.fromEmail || acc.username || 'Account'}</Typography>
                  <Typography variant="caption" color="text.secondary">{acc.fromEmail || acc.username}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No accounts selected.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAccountsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignPlanner;
